import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AdminFreeProduct, AdminProduct, AdminProductCatalog } from "@ternary/contracts"
import {
  Badge,
  Button,
  type Column,
  ConfirmDialog,
  EmptyState,
  Menu,
  type MenuItem,
  PageHeader,
  Pagination,
  Table,
  useToast,
} from "@ternary/ui"
import { useEffect, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { deleteAdminProducts, loadAdminProducts } from "../api.js"
import { CommerceTabs } from "../components/commerce-tabs.js"
import { DocsLink } from "../components/docs-link.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { NavIcon } from "../components/nav-icons.js"
import { paidPriceLabel, signupUrl, storefrontProductUrl } from "../product-draft.js"

const PRODUCTS_PER_PAGE = 10

/** One row: a free product (a signup page) or a paid one (sold on the storefront). */
type Row = { kind: "free"; product: AdminFreeProduct } | { kind: "paid"; product: AdminProduct }

function rowsOf(catalog: AdminProductCatalog): Row[] {
  return [
    ...catalog.freeProducts.map((product) => ({ kind: "free" as const, product })),
    ...catalog.products.map((product) => ({ kind: "paid" as const, product })),
  ]
}

/** Where members find the product: the signup page, or the paid product on the storefront. */
function productUrl(catalog: AdminProductCatalog, row: Row) {
  return row.kind === "free"
    ? signupUrl(catalog.storefrontUrl, row.product.slug)
    : storefrontProductUrl(catalog.storefrontUrl, row.product.slug)
}

/** A product's link as the list shows it: no scheme, and a storefront password masked. */
function shownUrl(url: string) {
  return url.replace(/^https?:\/\//, "").replace(/([?&]password=)[^&#]*/, "$1••••")
}

function roleNames(catalog: AdminProductCatalog, row: Row) {
  if (row.kind === "paid") {
    if (row.product.productType === "manual") return "Manual fulfilment"
    const ids = row.product.roles
    return ids.map((id) => catalog.roles.find((role) => role.id === id)?.name ?? id).join(", ")
  }
  const { guildId, roleIds } = row.product.access
  const roles = catalog.guilds.find((guild) => guild.id === guildId)?.roles ?? []
  if (roleIds.length === 0) return "None"
  return roleIds.map((id) => roles.find((role) => role.id === id)?.name ?? id).join(", ")
}

/**
 * The products: free ones, whose page is a signup page, then the paid ones. Create product opens
 * the wizard; each row's menu edits, copies the link, or deletes after a ConfirmDialog.
 */
export function AdminProductsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const query = useQuery({ queryFn: loadAdminProducts, queryKey: ["admin-products"] })
  const [pageIndex, setPageIndex] = useState(0)
  const [deleting, setDeleting] = useState<Row | null>(null)
  const { toast } = useToast()
  const [actionError, setActionError] = useState("")
  const sectionRef = useRef<HTMLElement>(null)
  // Where focus goes once a deleted row has left the list: that row’s Actions button went with it.
  const [refocus, setRefocus] = useState<{ label: string | null } | null>(null)
  const remove = useMutation({
    mutationFn: (row: Row) => deleteAdminProducts([row.product.id]),
    onMutate() {
      setActionError("")
    },
    async onSuccess(_data, row) {
      // The row that takes the deleted one’s place, chosen before the list changes.
      const at = rows.findIndex((each) => each.product.id === row.product.id)
      const neighbour = rows[at + 1] ?? rows[at - 1]
      setDeleting(null)
      toast({ message: `${row.product.name} deleted.`, tone: "success" })
      // The first product is on the dashboard's setup checklist (G7).
      void queryClient.invalidateQueries({ queryKey: ["admin-setup"] })
      await queryClient.invalidateQueries({ queryKey: ["admin-products"] })
      await queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] })
      setRefocus({ label: neighbour ? `Actions for ${neighbour.product.name}` : null })
    },
    onError(error) {
      setDeleting(null)
      setActionError(error instanceof Error ? error.message : "The product could not be deleted.")
    },
  })

  useEffect(() => {
    const section = sectionRef.current
    const active = document.activeElement
    // Only focus that was lost with the row: never pull it from where the user moved it.
    if (!refocus || !section || (active && active !== document.body && active.isConnected)) return
    const next = [...section.querySelectorAll("button")].find(
      (button) => button.getAttribute("aria-label") === refocus.label,
    )
    const heading = section.querySelector<HTMLElement>("h1")
    if (next) next.focus()
    else if (heading) {
      heading.tabIndex = -1
      heading.focus()
    }
  }, [refocus])

  const catalog = query.data
  const rows = catalog ? rowsOf(catalog) : []
  const pageCount = Math.max(1, Math.ceil(rows.length / PRODUCTS_PER_PAGE))
  const page = Math.min(pageIndex, pageCount - 1)
  const visible = rows.slice(page * PRODUCTS_PER_PAGE, (page + 1) * PRODUCTS_PER_PAGE)
  const canCreate = Boolean(catalog && (catalog.freeWritable || catalog.writable))
  const createLink = (
    <Link className="tny-button tny-button-primary" to="/dashboard/products/new">
      Create product
    </Link>
  )

  async function copyLink(url: string, copied: string) {
    setActionError("")
    try {
      await navigator.clipboard.writeText(url)
      toast({ message: copied, tone: "success" })
    } catch {
      setActionError(`The link could not be copied: ${url}`)
    }
  }

  function canEdit(row: Row) {
    return Boolean(row.kind === "free" ? catalog?.freeWritable : catalog?.writable)
  }

  function menuItems(row: Row): MenuItem[] {
    const writable = canEdit(row)
    const items: MenuItem[] = [
      {
        disabled: !writable,
        label: "Edit",
        onSelect: () => navigate(`/dashboard/products/${row.product.id}/edit`),
      },
    ]
    if (catalog) {
      // The real link, password and all: only the list's text masks it.
      items.push(
        row.kind === "free"
          ? {
              label: "Copy signup link",
              onSelect: () => void copyLink(productUrl(catalog, row), "Signup link copied."),
            }
          : {
              label: "Copy storefront link",
              onSelect: () => void copyLink(productUrl(catalog, row), "Storefront link copied."),
            },
      )
    }
    items.push({
      danger: true,
      disabled: !writable,
      label: "Delete",
      onSelect: () => {
        setActionError("")
        setDeleting(row)
      },
    })
    return items
  }

  const columns: Column<Row>[] = [
    {
      // A link only where the row’s Edit is allowed, as in its menu.
      cell: (row) =>
        canEdit(row) ? (
          <Link to={`/dashboard/products/${row.product.id}/edit`}>{row.product.name}</Link>
        ) : (
          row.product.name
        ),
      header: "Product",
      key: "name",
    },
    {
      cell: (row) =>
        row.kind === "free" ? (
          <Badge tone="success">Free</Badge>
        ) : (
          paidPriceLabel(
            row.product.priceMinor,
            row.product.currency,
            row.product.interval,
            row.product.intervalCount,
          )
        ),
      header: "Pricing",
      key: "pricing",
    },
    {
      cell: (row) => {
        if (!catalog) return null
        const url = productUrl(catalog, row)
        return (
          <a className="admin-external" href={url} rel="noreferrer" target="_blank">
            {shownUrl(url)}
            <NavIcon name="external" />
            <span className="tny-visually-hidden"> (opens in a new tab)</span>
          </a>
        )
      },
      header: "Link",
      key: "link",
      priority: 2,
    },
    {
      cell: (row) => (catalog ? roleNames(catalog, row) : null),
      header: "Roles",
      key: "roles",
      priority: 3,
    },
    {
      align: "end",
      cell: (row) => (
        <Menu
          icon={<NavIcon name="more" />}
          items={menuItems(row)}
          label={`Actions for ${row.product.name}`}
        />
      ),
      header: "Actions",
      key: "actions",
    },
  ]

  return (
    <section className="admin-stack admin-page" ref={sectionRef}>
      <PageHeader
        actions={
          <>
            <DocsLink />
            {/* While the list loads it holds its place, disabled. An empty list's own CTA is
                the empty state's, so the header shows none. */}
            {catalog && rows.length === 0 && !query.isError ? null : catalog && canCreate ? (
              createLink
            ) : (
              <Button disabled>Create product</Button>
            )}
          </>
        }
        description="What members sign up for and buy."
        title="Products"
      />
      <CommerceTabs active="products" />
      {catalog && !canCreate ? (
        <p className="admin-note admin-inline-links">
          Creating products needs a connected Discord server.{" "}
          <Link to="/dashboard/discord">Connect Discord</Link>
        </p>
      ) : null}
      {actionError ? (
        <p className="tny-error" role="alert">
          {actionError}
        </p>
      ) : null}
      {catalog && rows.length === 0 && !query.isError ? (
        <EmptyState
          action={canCreate ? createLink : undefined}
          icon={<NavIcon name="products" />}
          description="A free product gives your community a signup page: members sign up, then join your Discord server."
          title="Create your first product"
        />
      ) : (
        <>
          <Table
            caption="Products"
            captionHidden
            columns={columns}
            error={
              query.isError ? (
                // The table's slot is the alert.
                <LoadError
                  detail={errorDetail(query.error)}
                  message="We couldn't load your products"
                  onRetry={() => void query.refetch()}
                  retrying={query.isFetching}
                  role="none"
                />
              ) : undefined
            }
            loading={query.isPending}
            loadingLabel="Loading products…"
            rowKey={(row) => row.product.id}
            rows={visible}
          />
          {rows.length > PRODUCTS_PER_PAGE ? (
            <Pagination
              hasNext={page < pageCount - 1}
              hasPrevious={page > 0}
              label="Products pages"
              onNext={() => setPageIndex(page + 1)}
              onPrevious={() => setPageIndex(page - 1)}
              summary={`${page * PRODUCTS_PER_PAGE + 1}–${page * PRODUCTS_PER_PAGE + visible.length} of ${rows.length}`}
            />
          ) : null}
        </>
      )}
      <ConfirmDialog
        body={
          deleting?.kind === "free"
            ? "Its signup page stops working. The customers who signed up keep their records."
            : "It is archived in Stripe and leaves your storefront. A product with active members can’t be deleted."
        }
        confirmLabel="Delete product"
        danger
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting)
        }}
        open={deleting !== null}
        pending={remove.isPending}
        title={`Delete ${deleting?.product.name ?? "this product"}?`}
      />
    </section>
  )
}
